/*
 * ICC Shopping — Popup Banner
 *
 * Custom element: <popup-banner>
 *
 * Ported from the Brooks Brothers implementation. A promotional popup gated on
 * three things, in this order:
 *   1. Frequency cap  — has the shopper already dismissed it inside the window?
 *   2. Cookie consent — the Pandectes dialog must be gone before we arm anything.
 *   3. Trigger        — immediate, time delay, or scroll depth.
 *
 * Config (all read from data-* on the host in connectedCallback):
 *   data-handle             storage key for this placement          (default 'default')
 *   data-trigger-type       immediate | delay | scroll              (default 'immediate')
 *   data-trigger-delay      seconds, when trigger-type=delay        (default 0, floored at 0)
 *   data-trigger-scroll     percent 0-100, when trigger-type=scroll (default 50, clamped)
 *   data-frequency          once_per_session | once_ever | every_n_days
 *                                                                   (default 'once_per_session')
 *   data-frequency-days     window length, when frequency=every_n_days (default 7)
 *   data-consent-selector   override for the consent dialog selector
 *
 * DOM hooks (children of the host):
 *   [data-dismiss]        click closes; the attribute value is the recorded
 *                         reason (e.g. data-dismiss="backdrop")
 *   [data-cta-click]      click closes with reason 'cta'
 *   [data-popup-dialog]   the dialog panel. Needs tabindex="-1" so it can take
 *                         programmatic focus. Focus is trapped inside it while
 *                         open, and Escape closes it.
 *
 * Recorded reasons: 'close' | 'backdrop' | 'cta'. Escape records 'close'.
 *
 * The host MUST render with the `hidden` attribute — this component only ever
 * clears it, it never sets the initial hidden state.
 *
 * Storage: a { [handle]: { dismissedAt, reason } } map so several popups can
 * share one key. The .v1 suffix reserves room for a schema migration.
 *
 * In the theme editor (Shopify.designMode) the frequency cap, the consent gate
 * and the configured trigger are all bypassed — the merchant sees the popup
 * immediately, and dismissing writes nothing to storage.
 *
 * WHY THE CONSENT LAYER IS NOT THE BBS ONE. Brooks Brothers runs Consentmo,
 * which renders the banner inside an open shadow root, so that code had to step
 * through `.shadowRoot` and observe it directly (MutationObserver does not cross
 * a shadow boundary). It also gated on a second, theme-owned `#m-cookie-banner`.
 * ICC has neither. Pandectes renders in the light DOM, so an ordinary body
 * observer sees it — but it is still injected at runtime by the app embed, so
 * "not in the DOM yet" on first paint must not be read as "consent is clear".
 * That is what the grace period below is for.
 */

(function () {
  'use strict';

  var SECONDS_PER_DAY = 86400;

  /* Every surface Pandectes can put in front of the shopper.

     The first three are the banner itself, from the `window` template in
     PandectesSettings:
       <div id="pandectes-banner" class="cc-window-wrapper cc-popup-wrapper" role="dialog">
         <div class="pd-cookie-banner-window cc-window …">…</div>
       </div>

     The rest are the preferences modal, and they are not optional. Pressing the
     banner's "กำหนด" / Settings button HIDES the banner and opens
     #pd-cp-preferences behind #gdpr-blocking-page-overlay — so a gate that
     watched only the banner reads that as "consent resolved" and drops the popup
     on top of the preferences dialog. Observed doing exactly that before these
     were added.

     Not listed on purpose: .cc-revoke, the persistent "reopen cookie settings"
     widget this store shows all session (`revokable: true`). It is consent UI,
     but it is a dismissible corner tab rather than something blocking the page,
     and matching it would hold the gate shut forever.

     Also not listed: this theme's own native "Cookie Banner" popup block
     (sections/popups.liquid, `{% when 'cookie' %}`, #t4s-popup__cookies-law).
     It is currently disabled in config/settings_data.json in favor of
     Pandectes, so it is a non-issue today — but this gate has no visibility
     into it. If it is ever re-enabled, add its selector here or the same
     stacked-modal bug this list exists to prevent can happen again. */
  var DEFAULT_CONSENT_SELECTOR = [
    '#pandectes-banner',
    '.cc-window-wrapper',
    '.pd-cookie-banner-window',
    '#pd-cp-preferences',
    '.pd-cp-ui-container',
    '#gdpr-blocking-page-overlay',
  ].join(', ');

  /* How long to wait for Pandectes to inject its banner before concluding there
     is no consent UI on this page. The app embed loads async, so "not found" on
     first paint means "not injected yet", NOT "already accepted" — resolving
     straight away would race the banner onto the screen. */
  var PANDECTES_GRACE_MS = 3000;

  /* Overlays owned by the Kalles theme. If one of these is up, the promotional
     popup waits rather than stacking a second modal on top of it. Verified
     against this theme: Magnific Popup (newsletter, age gate, exit intent),
     quick view / quick shop, and the drawer family (menu, search, cart). */
  var THEME_OVERLAY_SELECTOR = [
    '.mfp-wrap',
    '.mfp-ready',
    '.t4s-modal--is-active',
    '.t4s-drawer[aria-hidden="false"]',
  ].join(', ');

  /* Is a consent banner currently on screen?
   *
   * Deliberately NOT using offsetParent: it is null for any position:fixed
   * element, which the Pandectes banner is, so it would report "gone" while the
   * banner is plainly visible. Test every way the app can actually hide it
   * instead — it reuses Osano-derived classes and may hide by class, inline
   * style, attribute, or by removing the node outright. */
  function isConsentVisible(el) {
    if (!el || !el.isConnected) return false;
    if (el.hasAttribute('hidden')) return false;
    if (el.getAttribute('aria-hidden') === 'true') return false;

    var style = window.getComputedStyle(el);
    if (style.display === 'none') return false;
    if (style.visibility === 'hidden' || style.visibility === 'collapse') return false;
    if (Number(style.opacity) === 0) return false;

    /* Laid out at all? Covers hiding that collapses the box without touching
       display, and nodes detached from the layout tree.

       The size test is not redundant with it. Once consent is given, Pandectes
       leaves #pandectes-banner in the DOM permanently as a flex wrapper that is
       display:flex, visibility:visible, opacity:1 and still reports one client
       rect — but that rect is 1440x0. Without the dimension check the gate reads
       that empty shell as "banner still on screen" and the popup never opens
       again for the rest of the session. */
    if (el.getClientRects().length === 0) return false;

    var rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  function findConsentEls(selector) {
    try {
      /* All matches, not the first. The selector intentionally covers both the
         outer wrapper and the inner banner window, and after consent the wrapper
         outlives the banner — so "is the first match visible?" answers the wrong
         question. Consent is up if ANY matched element is genuinely visible. */
      return Array.prototype.slice.call(document.querySelectorAll(selector));
    } catch (e) {
      /* A merchant-supplied data-consent-selector could be invalid; treat a bad
         selector as "no consent UI" rather than throwing and wedging the gate. */
      return [];
    }
  }

  function anyConsentVisible(selector) {
    return findConsentEls(selector).some(isConsentVisible);
  }

  /* Storage helpers.
     Every access is wrapped — quota errors and private mode must degrade to
     "not suppressed", never throw. */

  function readRecords(storage, key) {
    try {
      var raw = storage.getItem(key);
      return raw ? JSON.parse(raw) : {};
    } catch (e) {
      return {};
    }
  }

  function writeRecord(storage, key, handle, record) {
    try {
      var all = readRecords(storage, key);
      all[handle] = record;
      storage.setItem(key, JSON.stringify(all));
    } catch (e) {
      // Quota, disabled, or private-mode — silently skip
    }
  }

  // Tab-reachable descendants, in DOM order.
  var FOCUSABLE_SELECTOR =
    'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  // Reads a numeric data-* attribute, falling back when absent or unparseable.
  function readNumber(value, fallback) {
    if (value === '' || value == null) return fallback;
    var parsed = Number(value);
    return isNaN(parsed) ? fallback : parsed;
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  var VALID_TRIGGERS = ['immediate', 'delay', 'scroll'];

  class PopupBanner extends HTMLElement {
    connectedCallback() {
      var self = this;

      /* Re-entry guard. connectedCallback fires on every insertion, and the
         theme editor re-inserts sections constantly; without this the listeners
         below stack up on one element. */
      if (this._wired) return;
      this._wired = true;

      /* One controller for every DOM listener this element registers, on itself
         or on document/window. disconnectedCallback aborts it, which removes
         them all at once — no per-listener bookkeeping to get wrong. Timers,
         observers and animation frames cannot be cancelled by a signal, so they
         are tracked as instance fields alongside it. */
      this._ac = new AbortController();
      var signal = this._ac.signal;
      this._triggerTimer = null;
      this._graceTimer = null;
      this._bodyObserver = null;
      this._bannerObserver = null;
      this._overlayObserver = null;
      this._focusFrame = null;
      this._consentResolved = false;
      this._triggerArmed = false;

      this.handle = this.dataset.handle || 'default';

      var requested = (this.dataset.triggerType || '').trim();
      this.triggerType = VALID_TRIGGERS.indexOf(requested) === -1 ? 'immediate' : requested;

      this.triggerDelay = Math.max(0, readNumber(this.dataset.triggerDelay, 0));
      this.triggerScroll = clamp(readNumber(this.dataset.triggerScroll, 50), 0, 100);
      this.frequency = this.dataset.frequency || 'once_per_session';
      this.frequencyDays = readNumber(this.dataset.frequencyDays, 7);
      this.consentSelector = this.dataset.consentSelector || DEFAULT_CONSENT_SELECTOR;

      this.isDesignMode = !!(window.Shopify && window.Shopify.designMode);

      // Frequency short-circuit — nothing else runs, the banner stays hidden
      if (!this.isDesignMode && this.isSuppressedByFrequency()) return;

      /* Wire dismiss before arming, so a popup that reveals immediately is never
         un-dismissable. */
      this.querySelectorAll('[data-dismiss]').forEach(function (el) {
        el.addEventListener(
          'click',
          function () {
            self.dismiss(el.dataset.dismiss || 'close');
          },
          { signal: signal }
        );
      });

      this.querySelectorAll('[data-cta-click]').forEach(function (el) {
        el.addEventListener(
          'click',
          function () {
            self.dismiss('cta');
          },
          { signal: signal }
        );
      });

      this._dialog = this.querySelector('[data-popup-dialog]');
      this._focusBeforeOpen = null;

      /* Escape closes — scoped to this popup while it is actually open, so
         several popups on a page never fight over one key press, and so we never
         swallow Escape from a Kalles overlay while we are hidden. Recorded as
         'close', same as the X button: keyboard and pointer are two routes to
         one intent, and the reason vocabulary stays at three. */
      document.addEventListener(
        'keydown',
        function (e) {
          if (self.hidden || !self.isConnected) return;
          if (e.key !== 'Escape') return;
          e.preventDefault();
          self.dismiss('close');
        },
        { signal: signal }
      );

      /* Focus trap — the dialog claims aria-modal="true", so Tab must not be
         able to walk out of it into the page behind. */
      if (this._dialog) {
        this._dialog.addEventListener(
          'keydown',
          function (e) {
            if (e.key !== 'Tab') return;

            var focusable = Array.prototype.slice.call(
              self._dialog.querySelectorAll(FOCUSABLE_SELECTOR)
            );
            if (!focusable.length) return;

            var first = focusable[0];
            var last = focusable[focusable.length - 1];

            /* reveal() focuses the dialog itself (tabindex="-1"), which is
               neither first nor last — without it in this test, the very first
               Shift+Tab after opening walks focus out of the dialog and into the
               page behind, contradicting aria-modal="true". */
            if (
              e.shiftKey &&
              (document.activeElement === first || document.activeElement === self._dialog)
            ) {
              e.preventDefault();
              last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
              e.preventDefault();
              first.focus();
            }
          },
          { signal: signal }
        );
      }

      /* Consent must clear before the trigger listener even attaches. If the
         element was disconnected while we were waiting (theme editor rebuild, JS
         route change), do nothing — armTrigger on a detached element registers
         listeners on nothing and holds more closures. */
      this.waitForConsent().then(function () {
        if (!self.isConnected) return;
        self.armTrigger();
      });
    }

    disconnectedCallback() {
      if (this._ac) this._ac.abort();

      clearTimeout(this._triggerTimer);
      clearTimeout(this._graceTimer);
      if (this._bodyObserver) this._bodyObserver.disconnect();
      if (this._bannerObserver) this._bannerObserver.disconnect();
      if (this._overlayObserver) this._overlayObserver.disconnect();
      if (this._focusFrame) cancelAnimationFrame(this._focusFrame);

      this._triggerTimer = null;
      this._graceTimer = null;
      this._bodyObserver = null;
      this._bannerObserver = null;
      this._overlayObserver = null;
      this._focusFrame = null;

      /* Re-arm the entry guard. connectedCallback bails out when _wired is set,
         so leaving it true after tearing everything down would leave the element
         permanently inert: put back in the DOM it would wire nothing, and the
         popup could never open again. Everything registered up there is torn
         down by now, so re-running connectedCallback rebinds exactly once with
         nothing left over. */
      this._wired = false;

      /* Scroll lock is on <body>, not on this element, so a popup removed while
         open would otherwise freeze the page for good. */
      if (!this.hidden) {
        document.body.classList.remove('popup-banner-open');
      }
    }

    // Frequency cap — read side

    isSuppressedByFrequency() {
      var record;

      if (this.frequency === 'once_per_session') {
        record = readRecords(sessionStorage, PopupBanner.SESSION_KEY)[this.handle];
        return !!record;
      }

      if (this.frequency === 'once_ever') {
        record = readRecords(localStorage, PopupBanner.STORAGE_KEY)[this.handle];
        return !!record;
      }

      if (this.frequency === 'every_n_days') {
        record = readRecords(localStorage, PopupBanner.STORAGE_KEY)[this.handle];
        if (!record || typeof record.dismissedAt !== 'number') return false;
        var elapsed = Math.floor(Date.now() / 1000) - record.dismissedAt;
        return elapsed < this.frequencyDays * SECONDS_PER_DAY;
      }

      return false;
    }

    // Consent gate

    waitForConsent() {
      var self = this;

      return new Promise(function (resolve) {
        if (self.isDesignMode) return resolve();

        /* Set the moment a Pandectes banner is seen actually *visible*. It is
           what makes the grace timeout safe: once we know the consent UI is up,
           the timer must never release the popup on its own — only the banner
           going away may do that.

           Deliberately not "the element exists": Pandectes injects the wrapper
           before it fills and shows the banner inside it, so treating mere
           presence as detection would release the popup in that gap and race the
           banner onto the screen. Presence with nothing visible waits out the
           grace period instead. */
        var sawVisible = false;

        function done() {
          if (self._consentResolved) return;
          self._consentResolved = true;

          if (self._bodyObserver) self._bodyObserver.disconnect();
          if (self._bannerObserver) self._bannerObserver.disconnect();
          clearTimeout(self._graceTimer);
          self._bodyObserver = null;
          self._bannerObserver = null;
          self._graceTimer = null;

          resolve();
        }

        function watchBanner(el) {
          if (self._bannerObserver) return;
          self._bannerObserver = new MutationObserver(check);
          self._bannerObserver.observe(el, {
            attributes: true,
            attributeFilter: ['class', 'style', 'hidden', 'aria-hidden'],
            childList: true,
            subtree: true,
          });
        }

        function check() {
          if (self._consentResolved) return;

          var els = findConsentEls(self.consentSelector);

          /* Watch the outermost match with subtree:true, so changes to the inner
             banner window are caught even once the wrapper is all that is left. */
          if (els.length) watchBanner(els[0]);

          if (els.some(isConsentVisible)) {
            sawVisible = true;
            return;
          }

          /* Nothing visible. If we watched the banner go away, release now; if we
             never saw one, wait — Pandectes may still be loading. */
          if (sawVisible) done();
        }

        /* Grace window elapsed: the async consent UI has had its chance to
           appear. Release only if it is not on screen — a timeout must never let
           the popup out from under a banner the shopper can still see. */
        self._graceTimer = setTimeout(function () {
          if (self._consentResolved) return;
          if (!anyConsentVisible(self.consentSelector)) done();
        }, PANDECTES_GRACE_MS);

        /* childList/subtree catches the app embed appending the banner; the
           banner's own observer above catches it being hidden or removed. */
        self._bodyObserver = new MutationObserver(check);
        self._bodyObserver.observe(document.body, { childList: true, subtree: true });

        check();
      });
    }

    // Trigger strategies

    armTrigger() {
      var self = this;

      // Guard: consent can resolve from several paths; only arm once.
      if (this._triggerArmed) return;
      this._triggerArmed = true;

      /* The editor is a preview surface, not a shopper journey — a merchant
         should not have to wait out a delay or scroll to see what they just
         configured. */
      if (this.isDesignMode) {
        this.reveal();
        return;
      }

      if (this.triggerType === 'delay') {
        this._triggerTimer = setTimeout(function () {
          self.reveal();
        }, this.triggerDelay * 1000);
        return;
      }

      if (this.triggerType === 'scroll') {
        var onScroll = function () {
          var max = document.documentElement.scrollHeight - window.innerHeight;
          var ratio = max > 0 ? window.scrollY / max : 1;
          if (ratio * 100 >= self.triggerScroll) {
            window.removeEventListener('scroll', onScroll);
            self.reveal();
          }
        };
        window.addEventListener('scroll', onScroll, {
          passive: true,
          signal: this._ac.signal,
        });
        /* Evaluate once straight away. The browser restores the scroll position
           on reload and on back-navigation without firing a scroll event, so a
           shopper returning to a page already past the threshold would otherwise
           never see the popup. */
        onScroll();
        return;
      }

      // immediate
      this.reveal();
    }

    // Visibility

    /* Kalles owns several overlays of its own (newsletter and other Magnific
       popups, quick view, the drawer family). Opening on top of one of them
       would trap the shopper under two stacked modals with two focus traps, so
       wait for theirs to close first. */
    isThemeOverlayOpen() {
      if (document.querySelector(THEME_OVERLAY_SELECTOR)) return true;
      var root = document.documentElement;
      return (
        root.classList.contains('is--opend-drawer') || root.classList.contains('t4s-lock-scroll')
      );
    }

    reveal() {
      // A timer or scroll event can outlive the element's presence in the DOM.
      if (!this.isConnected) return;

      if (this.isThemeOverlayOpen()) {
        this.deferUntilOverlayCloses();
        return;
      }

      if (this._overlayObserver) {
        this._overlayObserver.disconnect();
        this._overlayObserver = null;
      }

      // Remember where focus was so dismiss() can hand it back.
      this._focusBeforeOpen = document.activeElement;

      this.hidden = false;
      document.body.classList.add('popup-banner-open');

      /* Move focus into the dialog, one frame later — the element is only
         focusable once it has actually been laid out. */
      var self = this;
      var dialog = this._dialog;
      if (dialog) {
        this._focusFrame = requestAnimationFrame(function () {
          self._focusFrame = null;
          dialog.focus();
        });
      }
    }

    deferUntilOverlayCloses() {
      var self = this;
      if (this._overlayObserver) return;

      this._overlayObserver = new MutationObserver(function () {
        if (self.isThemeOverlayOpen()) return;
        self._overlayObserver.disconnect();
        self._overlayObserver = null;
        self.reveal();
      });

      /* Kalles signals open/closed with classes and aria-hidden on the overlay
         and on <html>, and Magnific adds and removes its wrapper outright. */
      this._overlayObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class'],
      });
      this._overlayObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'aria-hidden'],
      });
    }

    dismiss(reason) {
      this.hidden = true;
      document.body.classList.remove('popup-banner-open');

      if (this._focusFrame) {
        cancelAnimationFrame(this._focusFrame);
        this._focusFrame = null;
      }

      /* Hand focus back to whatever had it before the popup opened, so a
         keyboard user does not get dumped at the top of the document. */
      var previous = this._focusBeforeOpen;
      this._focusBeforeOpen = null;
      if (previous && typeof previous.focus === 'function' && previous.isConnected) {
        previous.focus();
      }

      // Never poison the merchant's storage while previewing in the editor
      if (this.isDesignMode) return;

      var record = {
        dismissedAt: Math.floor(Date.now() / 1000),
        reason: reason,
      };

      if (this.frequency === 'once_per_session') {
        writeRecord(sessionStorage, PopupBanner.SESSION_KEY, this.handle, record);
      } else {
        writeRecord(localStorage, PopupBanner.STORAGE_KEY, this.handle, record);
      }
    }
  }

  PopupBanner.STORAGE_KEY = 'icc.popup.dismissed.v1';
  PopupBanner.SESSION_KEY = 'icc.popup.session.v1';

  if (!customElements.get('popup-banner')) {
    customElements.define('popup-banner', PopupBanner);
  }
})();
